import React, { useEffect, useState } from 'react';
import CustomButton from '../../components/shared/CustomButton/CustomButton';
import CustomTextField from '../../components/shared/CustomTextField/CustomTextField';
import zeplinColor from '../../theme/Color';
import styles from './LoginScreen.module.css';

import EntryLayoutScreen from '../../components/shared/EntryLayout/EntryLayout';

import { Link, useNavigate } from 'react-router-dom';
import validator from 'validator';
import { useInput } from '../../customHook/useInput';
import { validatePassword } from '../../helper/validate';
import RoutePaths from '../../routes/RoutePaths';
import { AuthService } from '../../service/auth.service';
import { useAppDispatch } from '../../store/hooks';
import { setRole } from '../../store/slices/authSlice';
import { Constants } from '../../utilities/constants';

import CustomPassword from '../../components/shared/CustomPassword/CustomPassword';

const LoginScreen = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const expiryTime = sessionStorage.getItem('loginExpiry') || '';
  sessionStorage.removeItem('reset-user');

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const {
    handleChange: handleEmailChange,
    handleBlur: handleEmailBlur,
    value: userName,
    invalid: invalidEmail,
    didChange: didEmailChange,
  } = useInput('', validator.isEmail);
  const {
    handleChange: handlePasswordChange,
    handleBlur: handlePasswordBlur,
    value: password,
  } = useInput('', (text) => validatePassword(text).valid);

  const valid = !invalidEmail && password.length;

  const [error, setError] = useState('');
  const [errorPassword, setErrorPassword] = useState('');
  const [open, setOpen] = React.useState(false);
  const [alert, setAlert] = useState('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const login = async () => {
    try {
      handleOpen();
      const res = await AuthService.login({ userName, password });

      if (res) {
        dispatch(setRole(res.role));
        sessionStorage.setItem('user-role', res.role);
        const huddleInfo = await AuthService.huddle_info(res.AuthenticationResult?.IdToken);

        sessionStorage.setItem('current-user', userName);
        sessionStorage.setItem('user-token', JSON.stringify(res.AuthenticationResult));
        sessionStorage.setItem('SFToken', JSON.stringify(res.isSFLinked || huddleInfo?.isSFLinked));
        sessionStorage.setItem('MSToken', JSON.stringify(res.isMSLogin));
        sessionStorage.setItem('isSlackConnected', huddleInfo?.isSlackLinked);
        sessionStorage.setItem('isGoogleConnected', huddleInfo?.isGoogleLinked);
        sessionStorage.setItem('isZoomConnected', huddleInfo?.isZoomLinked);
        sessionStorage.setItem('user-name', `${res.firstName}`);
        const now = new Date();
        const fiveHoursLater = new Date(now.getTime() + 60 * 60 * 1000 * 5);
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
        sessionStorage.setItem('loginExpiry', fiveHoursLater.toString());
        sessionStorage.setItem('refreshToken', oneHourLater.toString());
        navigate(RoutePaths.Home);
      }
    } catch (err: any) {
      const error = err?.response?.data?.name ? err?.response?.data?.name : err?.response?.data;
      if (err === 'Network Error') {
        setAlert('Network Error');
        setError('');
        setErrorPassword('');
      } else if (err?.response?.data) {
        setError(String(error));
        setErrorPassword('');
      } else if (error === Constants.ErrorType.NotAuthourized) {
        setErrorPassword(Constants.ErrorText.NotAuthorizedException);
        setError('');
      } else {
        setErrorPassword('');
        setError(Constants.ErrorText.UserNotFoundException);
      }
    } finally {
      handleClose();
    }
  };

  const children = (
    <div className={styles['login-input']}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 24 }}>
        <CustomTextField
          style={{ width: '50%', margin: 'auto' }}
          onBlur={handleEmailBlur}
          onChange={handleEmailChange}
          name='userName'
          text={userName}
          error={error ? error : invalidEmail && didEmailChange ? 'Please enter valid email' : ''}
          label={'Email address'}
          type='email'
          tabindex={1}
        />
        <CustomPassword
          style={{ width: '50%', margin: 'auto' }}
          showPassword={showPassword}
          handleClickShowPassword={handleClickShowPassword}
          handleMouseDownPassword={handleMouseDownPassword}
          handleMouseUpPassword={handleMouseUpPassword}
          onBlur={handlePasswordBlur}
          onChange={handlePasswordChange}
          name='password'
          text={password}
          label='Password'
          error={errorPassword}
          tabindex={2}
        />
      </div>
      <div className={styles.helperlogin}>
        <Link to={RoutePaths.ForgotPassword}>
          <CustomButton
            disabled={false}
            variant='text'
            text={'Forgot Password?'}
            textStyle={{ color: zeplinColor.Brand1 }}
            onClick={() => {}}
            tabindex={3}
          />
        </Link>
      </div>
      <CustomButton
        disabled={valid ? false : true}
        onClick={login}
        variant='contained'
        color={valid ? 'primary' : 'customDisable'}
        style={{
          width: '50%',
          borderRadius: '14px',
          height: 56,
          '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
        }}
        textStyle={{ color: valid ? zeplinColor.Neutral1 : zeplinColor.Neutral8 }}
        text={'Log In'}
      />
    </div>
  );

  const alternative = (
    <div
      style={{ margin: '0px auto 0px', width: '50%', display: 'flex', justifyContent: 'center' }}
    >
      <Link
        to={`https://${process.env.REACT_APP_M365}`}
        style={{ textDecoration: 'none', width: '100%' }}
      >
        <CustomButton
          disabled={false}
          variant='outlined'
          color='customSecondary'
          style={{
            color: zeplinColor.BasicBlack,
            width: '100%',
            margin: 'auto',
            borderRadius: '14px',
            height: 56,
            borderWidth: 1,
            borderColor: zeplinColor.Neutral9,
            display: 'flex',
            gap: '16px',
            '&:hover': {
              backgroundColor: zeplinColor.Neutral5,
            },
          }}
          text={'Continue with Microsoft'}
          iconPosition='start'
          icon={<img src={'/assets/microsoft-logo.svg'} />}
          onClick={() => {}}
        />
      </Link>
    </div>
  );

  useEffect(() => {
    if (expiryTime) {
      if (new Date() < new Date(expiryTime)) {
        navigate(RoutePaths.Home);
      }
    }
  }, []);

  return (
    <EntryLayoutScreen
      alert={alert}
      header={'Log in'}
      subheader='Enter your email and password to continue.'
      input={children}
      alternative={alternative}
      footerText='Don’t have an account?  '
      footerButtonText='Create Account'
      path={RoutePaths.Register}
      clickAction={() => {}}
      handleCloseModal={handleClose}
      open={open}
    />
  );
};

export default LoginScreen;
